// Felder des Leads (Close „CUSTOM FIELDS“): angezeigt werden nur ausgefüllte Felder, 1:1 in der Reihenfolge der
// Close-Organisationseinstellung (position), wie in Close selbst.
// „+“ bzw. „Feld hinzufügen ▾“ wählt ein leeres Feld zum Ausfüllen. Menü „…“: „Alle Felder zeigen“ (vorübergehend, wie
// Close „Reveal all fields“) und „Felder auswählen …“: je Nutzer immer zeigen / nur wenn ausgefüllt / nie (users.settings).
// Jedes Feld per Klick bearbeitbar; gespeichert wird bei Enter, beim Klick woanders und beim Wechsel ins nächste Feld
// (immer der zuletzt geschriebene Wert), ohne Hinweistext; Esc verwirft. Speichern schreibt nach Close (Schlüssel des Handelnden).
// Felder mit Rollenbeschränkung sind gesperrt.
// Beim Drüberfahren (wie Close, 19.10.2026): Kopieren, Lupe (Lead-Liste mit Filter „Feld = Wert“) und Löschen (leert das Feld
// sofort, ohne Nachfrage; „Rückgängig“ in der Meldung).
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { leadsApi, type CustomField, type FeldWert, type Kontakt, type Lead } from "../../api";
import { useAuth } from "../../auth/AuthContext";
import { useMeta } from "../../daten/meta";
import { customWert, darfAendern, feldFehler, feldText, istLeer } from "../../werkzeuge/felder";
import { htmlZuText, istHtml, sicheresHtml, textZuHtml } from "../../werkzeuge/html";
import { Auswahl } from "../Auswahl";
import { Dialog } from "../Dialog";
import { FeldEingabe } from "../FeldEingabe";
import { Icon } from "../Icon";
import { useMeldung } from "../Meldungen";
import { MenueKnopf } from "../Menue";
import { Avatar, KopierKnopf, ScoreAbzeichen } from "../Bausteine";
import { feldSuchAbfrage } from "../smartview/filterModell";
import { scoreStufe } from "../../werkzeuge/statusFarben";
import { Abschnitt } from "./Abschnitt";
import { modusVon, useFeldAuswahl, type FeldModus } from "./feldAuswahl";

interface Props {
  lead: Lead;
  kontakte: Kontakt[];
  onLead: (l: Lead) => void;
}

const TYP_TEXT: Record<string, string> = {
  text: "Text",
  textarea: "Text, mehrzeilig",
  richtextarea: "Text, formatiert",
  number: "Zahl",
  date: "Datum",
  datetime: "Datum und Uhrzeit",
  choices: "Auswahl",
  user: "Nutzer",
  contact: "Kontakt",
};

export function FelderAbschnitt({ lead, kontakte, onLead }: Props) {
  const meta = useMeta();
  const { nutzer } = useAuth();
  const meldung = useMeldung();
  const auswahl = useFeldAuswahl();
  // vorübergehend alle Felder (Close „Reveal all fields“), gilt bis zum Wechsel des Leads
  const [alle, setAlle] = useState(false);
  // über „Feld hinzufügen“ gewählte leere Felder (bleiben stehen, bis sie wieder leer verlassen werden)
  const [zusatz, setZusatz] = useState<string[]>([]);
  const [fuerLead, setFuerLead] = useState(lead.id);
  if (fuerLead !== lead.id) {
    setFuerLead(lead.id);
    setAlle(false);
    setZusatz([]);
  }
  const [einstellen, setEinstellen] = useState(false);
  const [bearbeiten, setBearbeitenRoh] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<FeldWert>(null);
  const aktivRef = useRef<string | null>(null);
  const setBearbeiten = (id: string | null) => {
    aktivRef.current = id;
    setBearbeitenRoh(id);
  };
  const [speichert, setSpeichert] = useState<string | null>(null);

  const felder = useMemo(
    () => [...(meta.meta?.custom_fields.lead ?? [])].filter((f) => f.type !== "hidden").sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, "de")),
    [meta.meta],
  );
  // Reihenfolge wie in Close (position der Organisationseinstellung), ohne eigene Vorrangliste
  const geordnet = felder;
  const befuellt = (f: CustomField) => !istLeer(customWert(lead, f.id));
  const modus = (f: CustomField) => modusVon(auswahl.einstellung, f.id);
  const sichtbar = geordnet.filter((f) => {
    if (bearbeiten === f.id || zusatz.includes(f.id)) return true;
    const m = modus(f);
    if (m === "nie") return alle;
    return befuellt(f) || m === "immer" || alle;
  });
  const anzahlBefuellt = felder.filter(befuellt).length;
  // zum Ausfüllen anbietbar: leer und gerade nicht sichtbar (auch ausgeblendete, dann mit Hinweis)
  const leereZumHinzufuegen = geordnet.filter((f) => !befuellt(f) && !sichtbar.includes(f) && darfAendern(f, nutzer?.role ?? "user"));
  const ausgeblendetBefuellt = alle ? 0 : felder.filter((f) => befuellt(f) && modus(f) === "nie").length;
  const leerVersteckt = felder.filter((f) => !befuellt(f) && !sichtbar.includes(f)).length;

  const hinzufuegen = (id: string) => {
    const f = felder.find((x) => x.id === id);
    if (!f) return;
    setZusatz((z) => (z.includes(id) ? z : [...z, id]));
    setEntwurf(null);
    setBearbeiten(id);
  };

  const kontaktName = (id: string) => kontakte.find((k) => k.id === id)?.name ?? "Kontakt";

  const zusatzWeg = (id: string) => setZusatz((z) => z.filter((x) => x !== id));
  const abbrechen = (f: CustomField) => {
    setBearbeiten(null);
    if (istLeer(customWert(lead, f.id))) zusatzWeg(f.id);
  };

  const speichern = async (f: CustomField, wert: FeldWert) => {
    if (aktivRef.current !== f.id) return;
    setBearbeiten(null);
    let w: FeldWert = istLeer(wert) ? null : wert;
    if (w === null) zusatzWeg(f.id);
    if (w !== null) {
      const fehler = feldFehler(f, w, false);
      if (fehler) {
        meldung.zeige("fehler", `${f.name}: ${fehler}`);
        return;
      }
    }
    if (f.type === "richtextarea" && typeof w === "string" && !istHtml(w)) w = textZuHtml(w);
    if (JSON.stringify(w) === JSON.stringify(customWert(lead, f.id) ?? null)) return;
    const vorher = lead;
    onLead({ ...lead, [`custom.${f.id}`]: w ?? undefined } as Lead);
    setSpeichert(f.id);
    try {
      const r = await leadsApi.aendern(lead.id, { custom: { [f.id]: w } });
      onLead(r.lead);
    } catch (e) {
      onLead(vorher);
      meldung.fehler(e, `${f.name} nicht gespeichert`);
    } finally {
      setSpeichert(null);
    }
  };

  const navigate = useNavigate();

  const leeren = async (f: CustomField) => {
    const alt = customWert(lead, f.id) ?? null;
    if (istLeer(alt)) return;
    const vorher = lead;
    onLead({ ...lead, [`custom.${f.id}`]: undefined } as Lead);
    setSpeichert(f.id);
    try {
      const r = await leadsApi.aendern(lead.id, { custom: { [f.id]: null } });
      onLead(r.lead);
      meldung.ok(`${f.name} gelöscht.`, {
        label: "Rückgängig",
        onClick: () => {
          void leadsApi
            .aendern(lead.id, { custom: { [f.id]: alt } })
            .then((x) => onLead(x.lead))
            .catch((e) => meldung.fehler(e, `${f.name} nicht wiederhergestellt`));
        },
      });
    } catch (e) {
      onLead(vorher);
      meldung.fehler(e, `${f.name} nicht gelöscht`);
    } finally {
      setSpeichert(null);
    }
  };

  const kopierText = (f: CustomField, wert: FeldWert): string => {
    if (istLeer(wert)) return "";
    if (typeof wert === "string" && istHtml(wert)) return htmlZuText(wert);
    return feldText(f, wert, { nutzerName: meta.nutzerName, kontaktName });
  };

  const zeile = (f: CustomField) => {
    const wert = customWert(lead, f.id);
    const darf = darfAendern(f, nutzer?.role ?? "user");
    const aktiv = bearbeiten === f.id;
    const ctx = { nutzerName: meta.nutzerName, kontaktName };
    const sofort = f.type === "choices" || f.type === "user" || f.type === "contact";
    return (
      <div key={f.id} className={`ls-feld${aktiv ? " ls-feld--aktiv" : ""}`}>
        <dt title={f.description ?? f.name}>{f.name}</dt>
        <dd>
          {aktiv ? (
            <div
              className="ls-feld__inline"
              onBlur={(e) => {
                // Text, Zahl, Datum: Verlassen des Felds speichert (wie in Close)
                if (sofort || e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                void speichern(f, entwurf);
              }}
            >
              <FeldEingabe
                def={f}
                wert={entwurf}
                kontakte={kontakte}
                darstellung="inline"
                autoFokus
                onChange={setEntwurf}
                onFertig={(w) => void speichern(f, w !== undefined ? w : entwurf)}
                onAbbrechen={() => abbrechen(f)}
              />
            </div>
          ) : (
            <button
              type="button"
              className={`ls-feld__wert${darf ? "" : " ls-feld__wert--gesperrt"}${speichert === f.id ? " ls-feld__wert--speichert" : ""}`}
              disabled={!darf}
              title={darf ? undefined : `Nur ${(f.editable_with_roles ?? []).join(", ")} darf dieses Feld ändern`}
              onClick={() => {
                setEntwurf(wert ?? null);
                setBearbeiten(f.id);
              }}
            >
              {istLeer(wert) ? (
                <span className="ls-feld__leer">{darf ? "Hinzufügen …" : "—"}</span>
              ) : f.type === "richtextarea" && typeof wert === "string" && istHtml(wert) ? (
                <span className="rich" dangerouslySetInnerHTML={{ __html: sicheresHtml(wert) }} />
              ) : f.type === "user" || f.type === "contact" ? (
                <span className="ls-pillen">
                  {(Array.isArray(wert) ? wert : [wert]).map((w) => {
                    const id = String(w);
                    const text = f.type === "user" ? meta.nutzerName(id) : kontaktName(id);
                    return (
                      <span key={id} className="ls-pille">
                        {f.type === "user" ? <Avatar name={text} id={id} groesse={16} /> : null}
                        <span className="ls-pille__text">{text}</span>
                      </span>
                    );
                  })}
                </span>
              ) : f.type === "textarea" ? (
                <span className="mehrzeilig">{typeof wert === "string" && istHtml(wert) ? htmlZuText(wert) : String(wert)}</span>
              ) : /score/i.test(f.name) && scoreStufe(wert) ? (
                <ScoreAbzeichen wert={wert} />
              ) : (
                feldText(f, wert, ctx)
              )}
              {!darf ? <Icon name="schloss" groesse={12} className="ls-feld__schloss" /> : null}
            </button>
          )}
          {!aktiv && !istLeer(wert) ? (
            <span className="ls-feld__aktionen">
              <KopierKnopf text={kopierText(f, wert ?? null)} label={`${f.name} kopieren`} />
              {feldSuchAbfrage(f.id, f.type, wert) ? (
                <button
                  type="button"
                  className="symbolknopf symbolknopf--klein"
                  title={`Leads mit diesem Wert suchen (${f.name})`}
                  aria-label={`Leads mit diesem Wert suchen (${f.name})`}
                  onClick={() => navigate(`/leads?feld=${encodeURIComponent(f.id)}&typ=${encodeURIComponent(f.type)}&wert=${encodeURIComponent(JSON.stringify(wert))}`)}
                >
                  <Icon name="suche" groesse={13} />
                </button>
              ) : null}
              {darf ? (
                <button
                  type="button"
                  className="symbolknopf symbolknopf--klein"
                  title={`${f.name} löschen`}
                  aria-label={`${f.name} löschen`}
                  disabled={speichert === f.id}
                  onClick={() => void leeren(f)}
                >
                  <Icon name="papierkorb" groesse={13} />
                </button>
              ) : null}
            </span>
          ) : null}
        </dd>
      </div>
    );
  };

  const hinzuOptionen = leereZumHinzufuegen.map((f) => ({
    wert: f.id,
    label: f.name,
    unter: `${TYP_TEXT[f.type] ?? f.type}${modus(f) === "nie" ? " · ausgeblendet" : ""}`,
  }));

  const hinzuKnopf = (klasse: string, inhalt: React.ReactNode, titel: string) => (
    <Auswahl
      optionen={hinzuOptionen}
      wert={null}
      onWahl={(w) => typeof w === "string" && hinzufuegen(w)}
      knopfKlasse={klasse}
      knopfInhalt={inhalt}
      titel={titel}
      deaktiviert={hinzuOptionen.length === 0}
      breite={280}
    />
  );

  return (
    <Abschnitt
      id="felder"
      icon="felder"
      titel="Felder"
      zahl={anzahlBefuellt}
      aktionen={
        <>
          {hinzuKnopf("symbolknopf symbolknopf--klein", <Icon name="plus" groesse={14} />, "Feld hinzufügen")}
          <MenueKnopf
            knopfKlasse="symbolknopf symbolknopf--klein"
            titel="Felder: alle zeigen, auswählen"
            ausrichtung="end"
            breite={240}
            punkte={[
              alle
                ? { label: "Nur ausgefüllte zeigen", icon: "felder", onClick: () => setAlle(false) }
                : { label: "Alle Felder zeigen", icon: "felder", rechts: `${felder.length}`, onClick: () => setAlle(true) },
              { label: "Felder auswählen …", icon: "einstellungen", onClick: () => setEinstellen(true) },
            ]}
          >
            <Icon name="mehr" groesse={14} />
          </MenueKnopf>
        </>
      }
    >
      {felder.length === 0 ? <p className="ls-leer">Keine Custom Fields für Leads.</p> : null}
      {sichtbar.length ? <dl className="ls-felder">{sichtbar.map(zeile)}</dl> : null}
      {felder.length ? (
        <div className="ls-felder__fuss">
          {hinzuKnopf("textknopf ls-felder__hinzu", <>Feld hinzufügen <Icon name="chevron-unten" groesse={12} /></>, "Leeres Feld auswählen und ausfüllen")}
          {alle ? (
            <button type="button" className="textknopf ls-felder__hinzu" onClick={() => setAlle(false)}>
              Nur ausgefüllte zeigen
            </button>
          ) : leerVersteckt || ausgeblendetBefuellt ? (
            <span className="ls-felder__info" title="Nur ausgefüllte Felder stehen da; „…“ › „Alle Felder zeigen“ zeigt den Rest">
              {[leerVersteckt ? `${leerVersteckt} leer` : null, ausgeblendetBefuellt ? `${ausgeblendetBefuellt} ausgeblendet` : null].filter(Boolean).join(", ")}
            </span>
          ) : null}
        </div>
      ) : null}
      <FelderAuswaehlen offen={einstellen} felder={geordnet} onSchliessen={() => setEinstellen(false)} />
    </Abschnitt>
  );
}

const MODI: { wert: FeldModus; label: string; titel: string }[] = [
  { wert: "immer", label: "Immer", titel: "Steht immer da, auch leer" },
  { wert: "befuellt", label: "Wenn ausgefüllt", titel: "Nur sichtbar, wenn ein Wert drinsteht (Standard)" },
  { wert: "nie", label: "Nie", titel: "Nie in der linken Spalte (über „Alle Felder zeigen“ erreichbar)" },
];

/** Kleine Einstellung je Nutzer: welche Felder links erscheinen. Gilt für alle Leads, sofort gespeichert. */
function FelderAuswaehlen({ offen, felder, onSchliessen }: { offen: boolean; felder: CustomField[]; onSchliessen: () => void }) {
  const auswahl = useFeldAuswahl();
  const meldung = useMeldung();
  const geaendert = auswahl.einstellung.ausgeblendet.length + auswahl.einstellung.immer.length;
  const setzen = (id: string, m: FeldModus) => void auswahl.setzen(id, m).catch((e) => meldung.fehler(e, "Feldauswahl nicht gespeichert"));
  return (
    <Dialog
      titel="Felder auswählen"
      offen={offen}
      onSchliessen={onSchliessen}
      breite={520}
      fuss={
        <>
          <button
            type="button"
            className="knopf knopf--leise"
            disabled={!geaendert}
            onClick={() => void auswahl.zuruecksetzen().catch((e) => meldung.fehler(e, "Nicht zurückgesetzt"))}
          >
            Standard (nur ausgefüllte)
          </button>
          <button type="button" className="knopf knopf--primaer" onClick={onSchliessen}>
            Fertig
          </button>
        </>
      }
    >
      <p className="ls-feldwahl__hinweis">Gilt für dich auf allen Leads. Standard: ein Feld erscheint nur, wenn es ausgefüllt ist.</p>
      <div className="ls-feldwahl" role="list">
        {felder.map((f) => {
          const m = modusVon(auswahl.einstellung, f.id);
          return (
            <div key={f.id} className="ls-feldwahl__zeile" role="listitem">
              <span className="ls-feldwahl__name" title={f.description ?? f.name}>
                {f.name}
                <span className="ls-feldwahl__typ">{TYP_TEXT[f.type] ?? f.type}</span>
              </span>
              <span className="ls-feldwahl__modi" role="radiogroup" aria-label={`${f.name}: anzeigen`}>
                {MODI.map((x) => (
                  <button
                    key={x.wert}
                    type="button"
                    role="radio"
                    aria-checked={m === x.wert}
                    title={x.titel}
                    className={`wahlknopf wahlknopf--klein${m === x.wert ? " wahlknopf--an" : ""}`}
                    onClick={() => m !== x.wert && setzen(f.id, x.wert)}
                  >
                    {x.label}
                  </button>
                ))}
              </span>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}
